/**
 * Shopify link handshake (source tier, fake-proof tested).
 * The embedded iframe (opener) vends a FRESH Shopify session token to the CI link page (child)
 * only through this protocol. Rules enforced here:
 *  - event.origin must equal the exact CI origin AND event.source must be the opened child
 *    (opener side) / the opener (child side); no wildcard targetOrigin, ever.
 *  - explicit hello nonce (child) + transaction id (opener) + per-request id; unknown/replayed
 *    request ids, wrong purposes, expired or over-budget transactions are ignored silently.
 *  - tokens are fetched fresh per request, never stored, logged, put in a URL or in storage.
 * It does not assume the opener survives sign-in navigation: a lost opener is an explicit state.
 */
export const MSG = {
  HELLO: 'ci-link/hello', READY: 'ci-link/ready', REQ: 'ci-link/token-request', TOKEN: 'ci-link/token',
};
export const PURPOSES = ['preview', 'confirm'];

const rid = () => {
  const a = new Uint8Array(16);
  (globalThis.crypto || window.crypto).getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
};

export function createOpenerBridge({ origin, getChild, getIdToken, now = Date.now, newId = rid, maxTokens = 4, ttlMs = 10 * 60 * 1000 }) {
  if (!origin || origin === '*' || origin === 'null') throw new Error('exact origin required');
  let txn = null; let started = 0; let used = 0; const seen = new Set();
  const reply = (child, msg) => child.postMessage(msg, origin);
  async function handleMessage(event) {
    const child = getChild();
    if (!child || child.closed) return;
    if (event.origin !== origin || event.source !== child) return;
    const d = event.data;
    if (!d || typeof d !== 'object' || typeof d.type !== 'string') return;
    if (d.type === MSG.HELLO) {
      if (txn !== null || typeof d.nonce !== 'string' || d.nonce.length < 16) return;   // one handshake per bridge
      txn = newId(); started = now();
      reply(child, { type: MSG.READY, nonce: d.nonce, txn });
      return;
    }
    if (d.type !== MSG.REQ || txn === null || d.txn !== txn) return;
    if (now() - started > ttlMs || used >= maxTokens) return;
    if (typeof d.reqId !== 'string' || d.reqId.length < 8 || seen.has(d.reqId)) return;
    if (!PURPOSES.includes(d.purpose)) return;
    seen.add(d.reqId); used += 1;
    let token;
    try { token = await getIdToken(); } catch (e) { return; }
    if (typeof token !== 'string' || !token) return;
    const again = getChild();
    if (again !== child || child.closed) return;
    reply(child, { type: MSG.TOKEN, txn, reqId: d.reqId, token });
  }
  return { handleMessage, _state: () => ({ txn, used }) };
}

export function createChildClient({ origin, getOpener, newId = rid, timeoutMs = 10000, setT = setTimeout, clearT = clearTimeout }) {
  if (!origin || origin === '*' || origin === 'null') throw new Error('exact origin required');
  let txn = null; const nonce = newId(); const pending = new Map();
  let readyResolve; let readyReject;
  const ready = new Promise((res, rej) => { readyResolve = res; readyReject = rej; });
  ready.catch(() => {});
  function handleMessage(event) {
    const op = getOpener();
    if (!op || event.origin !== origin || event.source !== op) return;
    const d = event.data;
    if (!d || typeof d !== 'object') return;
    if (d.type === MSG.READY && txn === null && d.nonce === nonce && typeof d.txn === 'string' && d.txn) {
      txn = d.txn; readyResolve(); return;
    }
    if (d.type === MSG.TOKEN && txn !== null && d.txn === txn && pending.has(d.reqId)) {
      const p = pending.get(d.reqId); pending.delete(d.reqId); clearT(p.timer);
      if (typeof d.token === 'string' && d.token) p.resolve(d.token); else p.reject(new Error('no token'));
    }
  }
  function connect() {
    const op = getOpener();
    if (!op || op.closed) return Promise.reject(new Error('NO_OPENER'));
    const timer = setT(() => readyReject(new Error('HANDSHAKE_TIMEOUT')), timeoutMs);
    op.postMessage({ type: MSG.HELLO, nonce }, origin);
    return ready.then(() => clearT(timer), (e) => { clearT(timer); throw e; });
  }
  function getToken(purpose) {
    const op = getOpener();
    if (txn === null || !op || op.closed) return Promise.reject(new Error('NO_OPENER'));
    if (!PURPOSES.includes(purpose)) return Promise.reject(new Error('bad purpose'));
    const reqId = newId();
    return new Promise((resolve, reject) => {
      const timer = setT(() => { pending.delete(reqId); reject(new Error('TOKEN_TIMEOUT')); }, timeoutMs);
      pending.set(reqId, { resolve, reject, timer });
      op.postMessage({ type: MSG.REQ, txn, reqId, purpose }, origin);
    });
  }
  return { handleMessage, connect, getToken };
}
